// @flow
import React, {Component, Fragment} from 'react';
import {Helmet} from 'react-helmet';
import Slider from 'react-slick/lib';
import {Subject} from 'rxjs';
import {Link} from 'react-router-dom';
import {debounceTime, tap} from 'rxjs/operators';
import PropTypes from 'prop-types';
import classNames from 'classnames/bind';
import {animateScroll as scroll, scroller} from 'react-scroll';
import {defineMessages, FormattedMessage, intlShape} from 'react-intl';
import {generateRandomNumber} from '../../../../utils/number';
import {UntilTablet} from '../../../../utils/responsive';
import NewProductForm from './NewProductForm/NewProductForm';
import ProductForm from './ProductForm/ProductForm';
import AmazonProductForm from './AmazonProductForm/AmazonProductForm';
import {
  amazonSpainVerifier,
  amazonUrlRegex,
  formatToTwoDecimal, isIphone13,
  shoppingCartUrlVerifier,
  urlVerifier,
} from '../../../../utils/string';
import {FormValidator} from '../../../../utils/form';
import queryString from 'query-string';
import {DEFAULT_COUNTRIES, getLocalizedCountry} from '../../../local-values/utils/countries';
import Cleave from 'cleave.js/dist/cleave-react';
import Modal from '../../../../elements/Modal/Modal';
import Loading from '../../../../elements/Loading/Loading';
import SuccessErrorModal from '../../../../elements/SuccessErrorModal/SuccessErrorModal';
import {Base64} from 'js-base64';
import update from 'immutability-helper';
import analytics from '../../../../analytics';
import {getChosenVariant} from '../../../ab-test/utils';
import VerificationBanner from '../components/VerificationBanner/VerificationBanner';

import style from './ProductStep.scss';
import formStyle from '../../../../assets/styles/form.scss';
import inputStyle from '../../../../assets/styles/input.scss';
import buttonStyle from '../../../../assets/styles/button.scss';
import yogi_sad from '../../../../assets/img/Yogi_sad2.png';
import yogi_paw from '../../../../assets/img/YOGI-QEMPO2.png';
import loadingAPNG from '../../../../assets/img/loading_apng.png';
import girl from '../../../../assets/img/new-order/product-step/girl.png';

import scrapAdvice1 from '../../../../assets/img/new-order/product-step/scrapping/Info.png';
import scrapAdvice2 from '../../../../assets/img/new-order/product-step/scrapping/Warranty.png';
import scrapAdvice3 from '../../../../assets/img/new-order/product-step/scrapping/seguridad.png';
import scrapAdvice4 from '../../../../assets/img/new-order/product-step/scrapping/envio.png';

import type {LocaleState} from '../../../locale/duck';
import type {Option} from '../../../../elements/Select/Select';
import type {
  OnlineStore, ScrappedDetailOptionItem,
  ScrappedProduct,
  User,
  WishList,
  WishListItem,
} from '../../../../entities';
import type {HTTPError} from '../../../../error';
import type {LocalValuesState} from '../../../local-values/duck';
import type {InputQuantityAction} from '../../../../elements/InputQuantity/InputQuantity';
import type {Location as RouterLocation} from '../../../../router';
import type {PostBagRequestApi, PostItemRequestApi} from '../../../bag/api';
import type {
  ShippingItemRequestApi,
  ShoppingCartItemRequestApi,
  ShoppingCartRequestApi,
  VirtualItem,
  VirtualItemRequestApi,
} from '../../../shopping-cart/api';
import type {AlertType} from '../../../alert/duck';
import type {FormValidationResult} from '../../../../utils/form';
import type {Forecast, ForecastPricesApiReq} from '../../QuoteStep/api';
import type {CancelablePromise} from '../../../../utils/promise';
import type {ForecastType} from './NewProductForm/QuoteBox/ForecastBox/ForecastBox';

type Props = {
  +user?: User;
  +authenticated?: boolean;
  +handleOpenAuthModal: (openLoginView?: boolean) => void;
  +authModalVisible?: boolean;
  +localeState: LocaleState;
  +localValues: LocalValuesState;
  +chosenVariants?: { [key: string]: number };
  +location: RouterLocation;
  +goToRedirect: (string) => void;
  +preloadedProduct: ScrappedProduct;
  +fetchWishLists: (userId: string) => void;
  +wishLists?: WishList[];
  +postProductToWishList: (wishListItem: WishListItem, nameSlug: string) => void;
  +postNewWishList: (wishListName: string, wishListItem: WishListItem) => void;
  +isPostingProduct?: boolean;
  +isPostingNewWishList?: boolean;
  +postProductSuccess?: boolean;
  +postNewWishListError?: HTTPError;
  +postBagItem: (bagRequest: PostBagRequestApi, bagId?: string, queryParams?: { [string]: string }) => void;
  +isPostingBag: boolean;
  +postBagItemError: HTTPError;
  +postVirtualShoppingCartItem: (virtualShoppingCartRequest: VirtualItemRequestApi) => void;
  +postShoppingCartItem: (shoppingCartRequest: ShoppingCartRequestApi) => void;
  +isPostingCartItem?: boolean;
  +postCartItemSuccess?: boolean;
  +postCartItemError?: HTTPError;
  +resetAlertState: () => void;
  +virtualShoppingCart: VirtualItem[];
  +virtualBag?: VirtualItem[];
  +fetchVirtualBag: () => void;
  +postVirtualBagItem: (virtualBagRequest: VirtualItemRequestApi, queryParams?: { [string]: string }) => void;
  +deleteVirtualBag: () => void;
  +scrapUrl: (url: string) => void;
  +isScrappingUrl?: boolean;
  +scrappedProduct?: ScrappedProduct;
  +scrapSuccess?: boolean;
  +scrapUrlError?: HTTPError;
  +setNewAlert: (id: string, alertType: AlertType, message?: string, messageIntl?: string) => void;
  +scrapResetState: () => void;
  +resetAlertErrorState: () => void;
  +forecastPrices: (forecastRequest: ForecastPricesApiReq) => void;
  +forecast?: Forecast;
  +isLoadingForecast?: boolean;
  +checkoutFetchCheckoutDataSuccess: (forecast: Forecast, selectedIndex: number, importMethod: 'CLASSIC' | 'DIY') => void;
};

type State = {
  /* global */
  arrivalCountry: {
    value: string;
    errors?: string[];
  };
  departureCountry: {
    value: string;
    errors?: string[];
  };
  preferredImportationMethod?: string;
  /* product */
  url: {
    value: string;
    errors?: string[];
  };
  image: {
    value?: string;
    valueURI?: string;
    errors?: string[];
  };
  name: {
    value: string;
    errors?: string[];
  };
  description: {
    value: string;
    errors?: string[];
  };
  shipping: {
    value: string;
    errors?: string[];
  };
  onlineStore: OnlineStore;
  price: {
    value: string;
    errors?: string[];
  };
  amount: {
    value: number;
    errors?: string[];
  };
  categoryId: {
    value: string;
    errors?: string[];
  };
  withBox: {
    value: number;
    errors?: string[];
  };
  weightUnit: {
    value: string;
  };
  weight: {
    value: string;
    valueKg: string; // To just fetch the desired value when needed.
    errors?: string[];
  };
  volumeTier: {
    value: ?number;
    code: string;
    errors?: string[];
  };
  /* product aux */
  loadingForecast: boolean;
  // forecast?: Forecast;
  modalOpen?: boolean;
  wishListOption?: string;
  wishListName: {
    value: string;
    errors?: string[];
  };
  showProductAdded?: boolean;
  // duplicate property in parent to save form validation state
  hideOptions: boolean;
  showError?: boolean;
  customShipping?: boolean;
  modalShipping?: boolean;
  isScrappingModalShouldAppear?: boolean;
  couldNotScrap: boolean;
  showSlider: boolean;
  detailOptions: {
    [key: string]: {
      value: string;
      errors?: string[];
      disabled?: {
        [key: string]: boolean;
      };
    };
  };
  formType: 'DEFAULT' | 'AMAZON';
  vendorData: { [key: string]: any };
  quoteMethodType?: ForecastType;
  quoteFullSubtype?: string;
  quoteLiteSubtype?: string;
  lastShoppingCartItemAdded?: {
    name: string;
    image: string;
    quoteMethodType: ForecastType;
    quoteFullSubtype?: string;
    quoteLiteSubtype?: string;
    // total: string;
  };
  shoppingCartItemAddedModal: boolean;
  notAvailableModal: boolean;
};

const KG_TO_LB_FACTOR = 2.205;

const DEFAULT_EMPTY_ITEM = {
  url: {
    value: '',
  },
  image: {},
  name: {
    value: '',
  },
  description: {
    value: '',
  },
  shipping: {
    value: '',
  },
  onlineStore: {},
  price: {
    value: '',
  },
  categoryId: {
    value: '',
  },
  amount: {
    value: 1,
  },
  weightUnit: {
    value: 'kg',
  },
  weight: {
    value: '',
    valueKg: '',
  },
  volumeTier: {
    value: undefined,
    code: '',
  },
  withBox: {
    value: 0,
  },
  detailOptions: {},
};

const SCROLL_OPTIONS = {
  duration: 350,
  delay: 0,
  smooth: 'easeInOutQuart',
  offset: -100,
};

const messages = defineMessages({
  arrivalCountryPlaceholder: {
    id: 'newOrder.product.arrivalCountry.placeholder',
  },
});

export default class ProductStep extends Component<Props, State> {
  formValidator: FormValidator;
  cancelableForecast: CancelablePromise<Forecast>;
  changesSubject$: Subject;
  changeUrlSubject$: Subject;
  nextButtonPressHoldAction: ?('addCart' | 'buyNow');
  shippingCleave: Cleave;
  priceCleave: Cleave;
  weightCleave: Cleave;
  lastScrappedProduct: ?ScrappedProduct;
  originType: string;
  scrapTimeoutHandle: TimeoutID;
  apds: string;
  preferredCourier: string;
  preferredFull: string;
  action: 'CART' | 'BUY_NOW';

  constructor(props: Props) {
    super(props);
    const {location, preloadedProduct, scrapUrl} = props;
    this.state = {
      ...DEFAULT_EMPTY_ITEM,
      departureCountry: {
        // TODO: CHANGEME!!!! this is hardcoded for now since all
        // TODO: product are offered from USA
        value: 'US',
      },
      arrivalCountry: {
        // TODO: is missing props.newOrder.cart.arrivalCountry
        value: props.localeState.domainCountry || '',
      },
      loadingForecast: false,
      wishListName: {value: ''},
      hideOptions: true,
      couldNotScrap: false,
      showSlider: false,
      detailOptions: {},
      formType: 'DEFAULT',
      vendorData: {},
      showProductAdded: false,
      shoppingCartItemAddedModal: false,
      notAvailableModal: false,
    };
    const name = {value: ''};
    const url = {value: ''};
    const parsedQuery = queryString.parse(location.search);
    if (!!parsedQuery.originType) {
      this.originType = parsedQuery.originType.toUpperCase();
    }
    
    if (!!parsedQuery.action) {
      this.action = parsedQuery.action;
    }
    
    if (!!parsedQuery.preferred_importation_method) {
      this.preferredImportationMethod = parsedQuery.preferred_importation_method;
      this.state.preferredImportationMethod = parsedQuery.preferred_importation_method;
      this.state.quoteMethodType = parsedQuery.preferred_importation_method === 'lite' ? 'LITE' : 'FULL';

      if (!!parsedQuery.preferred_courier) {
        this.preferredCourier = parsedQuery.preferred_courier;
        this.state.quoteLiteSubtype = parsedQuery.preferred_courier;
      }
      
      if (!!parsedQuery.preferred_full) {
        this.preferredFull = parsedQuery.preferred_full;
        this.state.quoteFullSubtype = parsedQuery.preferred_full;
      }
    }
    if (!!parsedQuery.apds) {
      this.apds = parsedQuery.apds;
    }

    this.state = {...this.state, name: {...name}, url: {...url}};
    
    this.formValidator = new FormValidator({
      arrivalCountry: {
        required: 'required',
      },
      departureCountry: {
        required: 'required',
      },
      image: {
        required: 'required',
      },
      url: {
        required: 'required',
        urlFormat: 'urlFormat',
        notCartUrlFormat: shoppingCartUrlVerifier,
        amazonSpain: amazonSpainVerifier,
      },
      name: {
        required: 'required',
      },
      price: {
        required: 'required',
      },
      weight: (null: any),
      categoryId: {
        required: 'required',
      },
      volumeTier: (null: any),
    });
    this.changesSubject$ = new Subject();
    this.changeUrlSubject$ = new Subject();

    if (parsedQuery.initialQuery) {
      const startsWithHTTPS = parsedQuery.initialQuery.startsWith('http');
      if (urlVerifier(parsedQuery.initialQuery) || startsWithHTTPS) {
        let urlValue = parsedQuery.initialQuery;
        if (urlValue.match(amazonUrlRegex)) {
          this.state = {...this.state, formType: 'AMAZON'};
        }
        if (!startsWithHTTPS) {
          urlValue = `https\://${parsedQuery.initialQuery}`;
        }
        if (!preloadedProduct) {
          this.state.url.value = parsedQuery.initialQuery;
          this.scrapProduct(urlValue, true);
        }
      } else {
        name.value = parsedQuery.initialQuery;
      }
    }
    
    console.log('initial state', JSON.stringify(this.state, null, 2));
  }

  componentDidMount() {
    const {
      preloadedProduct, user, authenticated, fetchVirtualBag, localeState, scrapUrl, resetAlertErrorState,
      forecastPrices,
    } = this.props;
    const {preferredImportationMethod} = this.state;
    const parsedQuery = queryString.parse(location.search);

    // Fire product reached
    analytics.fireCustomEvent('Product Step Viewed', {
      category: 'Product Step',
      action: 'Viewed',
      user_type: 'purchaser',
      user_referrer: window.localStorage.getItem('userReferrer') || '',
      landing_url: window.localStorage.getItem('landingUrl') || '',
      domain_country: localeState.domainCountry,
    });

    // NOTE: This subject used to send a request to forecast the lowest price of the product
    this.changesSubject$.pipe(
      tap(() => {
        const req = this.buildForecastRequest();
        if (req) {
          if (!preferredImportationMethod) {
            console.log('setting null !!!')
            this.setState({
              quoteMethodType: undefined,
              quoteFullSubtype: undefined,
              quoteLiteSubtype: undefined,
            });
          }
        }
      }),
      debounceTime(500)
    ).subscribe(() => {
      const req = this.buildForecastRequest();

      if (req) {
        scroller.scrollTo('quote', SCROLL_OPTIONS);
        forecastPrices(req);
      }
    });

    this.changeUrlSubject$.pipe(debounceTime(1000)).subscribe(() => {
      const {url} = this.state;
      if (!url.errors && url.value) {
        let args = {};
        if (url.value.match(amazonUrlRegex)) {
          args.formType = {$set: 'AMAZON'};
        } else {
          args.url = {$set: {value: url.value || ''}};
        }
        this.resetState(() => {
          this.cancelForecast();
          resetAlertErrorState();
          this.scrapProduct(url.value);
        }, args);
      }
    });
    
    if (parsedQuery.product) {
      try {
        const b = Base64.decode(parsedQuery.product);
        const p = JSON.parse(b);
        this.modifyStateAfterScrap(p, true);
      } catch (e) {
        console.log('Error parsing b64 product', e);
      }
    } else if (preloadedProduct) {
      this.modifyStateAfterScrap(preloadedProduct, true);
    }

    //TODO: why are you fetching here?
    if (!authenticated || !user) {
      fetchVirtualBag();
    }
  }

  componentWillUnmount() {
    if (this.changesSubject$) {
      this.changesSubject$.unsubscribe();
    }
    if (this.changeUrlSubject$) {
      this.changeUrlSubject$.unsubscribe();
    }
    this.cancelForecast();
    const {resetAlertState} = this.props;
    resetAlertState();
  }

  componentDidUpdate(prevProps: Props, prevState: State) {
    const {
      authModalVisible, user, authenticated, fetchWishLists, postProductSuccess, postNewWishListError,
      postCartItemSuccess, postBagItemError, postCartItemError, virtualBag, goToRedirect,
      scrapSuccess, scrappedProduct, scrapUrlError, setNewAlert, localValues, localeState, forecast
    } = this.props;
    const {modalOpen, couldNotScrap, categoryId, url} = this.state;
    
    // If just finished loading forecast
    if (prevProps.forecast !== forecast) {
      console.log('action');
      if (this.action) {
        if (this.action === 'CART') {
          this.handleClickAddCart();
        }
        if (this.action === 'BUY_NOW') {
          this.handleClickBuyNow();
        }
      }
    }

    // If just finished loading categories
    if (!prevProps.localValues.categoriesMap && localValues.categoriesMap) this.changesSubject$.next();

    // This means it just got the domainCountry
    if (!prevProps.localeState.domainCountry && !!localeState.domainCountry) {
      // If it does not have the arrivalCountry set up, it means it just figured out
      this.setState({
        arrivalCountry: {
          value: localeState.domainCountry || '',
        },
      });
    }

    // This means it was just authenticated and came from a "Next" button click
    if (this.nextButtonPressHoldAction && user && authenticated) {
      if (this.nextButtonPressHoldAction === 'buyNow') {
        this.handleClickBuyNow();
      }
      if (this.nextButtonPressHoldAction === 'addCart') {
        this.handleClickAddCart();
      }
      this.nextButtonPressHoldAction = undefined;
    }

    // This means: when the modal closes
    if (authModalVisible === false && prevProps.authModalVisible === true) {
      this.nextButtonPressHoldAction = undefined;
    }
    if (!prevProps.authenticated && authenticated && user) {
      // fetchWishLists(user.id);
    }
    if (!prevProps.postProductSuccess && postProductSuccess) {
      this.setState({showProductAdded: true});
    }
    if (prevProps.postNewWishListError !== postNewWishListError) {
      this.setState({showError: true, modalOpen: false});
    }
    if (!prevProps.postCartItemSuccess && postCartItemSuccess) {
      this.setState({shoppingCartItemAddedModal: true});
      this.resetState();
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
    // When just finished scrapping
    if (!prevProps.scrapSuccess && scrapSuccess && !!scrappedProduct) {
      this.modifyStateAfterScrap(scrappedProduct);
      if (!couldNotScrap) {
        this.setState({
          showSlider: false,
          isScrappingModalShouldAppear: false,
        });
      }
    }
    if (prevProps.scrapUrlError !== scrapUrlError && !!scrapUrlError) {

      if (this.scrapTimeoutHandle && window) window.clearTimeout(this.scrapTimeoutHandle);

      const up: { [key: string]: any } = {
        isScrappingModalShouldAppear: {$set: true},
      };
      const unscrappedFields = this.getUnscrappedFields({});
      unscrappedFields.forEach((f: string) => {
        up[f] = {errors: {$set: ['unscrapped']}};
      });

      if (scrapUrlError.data) {
        switch (scrapUrlError.data.name) {
          case 'UrlPathBlacklistError':
            up.url = {errors: {$set: ['notCartUrlFormat']}};
            break;
          case 'EbayAuctionError':
            up.url = {errors: {$set: ['ebayAuction']}};
            break;
          case 'ScrappingError':
            up.couldNotScrapModalOpen = {$set: true};
            break;
        }
      }
      if (Math.round(scrapUrlError.code / 100) === 5) {
        setNewAlert('new-order', 'error', undefined, 'httpErrors.post.500');
        up.formType = {$set:'DEFAULT'};
        // up.couldNotScrap = true;
        const parsed = queryString.parseUrl(scrapUrlError.url);
        up.url = {...up.url, value: {$set: parsed.query.url}};
      } else if (scrapUrlError.code === 400) {
        up.formType = {$set: 'DEFAULT'};
        // up.couldNotScrap = true;
        const parsed = queryString.parseUrl(scrapUrlError.url);
        up.url = {...up.url, value: {$set: parsed.query.url}};
      }

      // delete url if not scrapped
      // delete up.url;

      this.setState((st: State) => update(st, up));
    }

    // If categoryId is set but categoriesMap is not ready yet
    if (localValues && !!categoryId.value &&
      !prevProps.localValues.categoriesMap && !!localValues.categoriesMap) {
      //parse in case value is a string
      this.updateFormValidator(parseInt(categoryId.value));
      if (!!localValues.categoriesMap &&
        localValues.categoriesMap[`${categoryId.value}`].criteria === 'VOLUME' &&
        this.lastScrappedProduct) {
        // new variable to avoid flow type error at findIndex()
        const volumeTier = this.lastScrappedProduct.volumeTier;
        const foundIndex = localValues.categoriesMap[`${categoryId.value}`]
          .volumeTiers.filter(x => x.enabled).findIndex(x => x.code === volumeTier);
        if (foundIndex !== -1) {
          this.setState((prevState: State) => update(prevState, {
            volumeTier: {$set: {value: foundIndex, code: volumeTier}},
          }));
        }
      }
    }

    // If there is a new alert scroll to top
    if ((!prevProps.postNewWishListError && postNewWishListError) ||
      (!prevProps.postBagItemError && postBagItemError) ||
      (!prevProps.postCartItemError && postCartItemError)) {
      scroll.scrollTo(0, {
        duration: 400,
      });
    }
  }

  scrapProduct = (url: string, doNotUseSetState: boolean = false) => {
    const {scrapUrl} = this.props;

    const errors = this.formValidator.validateField('url', url);

    if (errors && errors.length) {
      if (doNotUseSetState) {
        this.state.url.value = url;
        this.state.url.errors = errors;
      }else {
        this.setState((st: State) =>
          update(st, {
            url: {
              value: {$set: url},
              errors: {$set: errors},
            },
          })
        );
      }
    } else {
      if (this.scrapTimeoutHandle && window) window.clearTimeout(this.scrapTimeoutHandle);
      const timeout = url.match(amazonUrlRegex) ? 0 : 3000;
      this.scrapTimeoutHandle = setTimeout(() => {
        this.setState({
          isScrappingModalShouldAppear: true,
        }/*, () => setTimeout(() => this.setState({showSlider: true}), 300)*/);
      }, timeout);
      scrapUrl(url);
    }

  };

  handleChangeArrivalCountry = (option: Option) => {
    this.setState({
      arrivalCountry: {
        value: `${option.value}`,
      },
    }, () => this.changesSubject$.next());
  };

  handleFocusCountrySelect = () => {
    const {arrivalCountry} = this.state;
    this.setState({
      arrivalCountry: {
        value: arrivalCountry.value,
      },
    });
  };

  handleChangeInputText = (e: Event, text?: string) => {
    let up = {};
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      up = {[name]: {value}};
    }
    if (e.target instanceof HTMLTextAreaElement && typeof text !== 'undefined') {
      const {name} = e.target;
      up = {[name]: {value: text}};
    }
    this.setState({...up}, () => this.changesSubject$.next());
  };

  handleChangeInputUrl = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      this.setState({
        [name]: {
          value,
        },
      }, () => this.changeUrlSubject$.next());
    }
  };

  handleOnKeyPress = (k: KeyboardEvent) => {
    const {charCode} = k;
    if (charCode === 13) {
      this.changeUrlSubject$.next();
    }
  };

  getUnscrappedFields = (product: ScrappedProduct) => {
    const {localValues} = this.props;
    const r = [];
    if (!product.url) r.push('url');
    if (!product.name) r.push('name');
    if (!product.image) r.push('image');
    if (!product.price) r.push('price');
    if (!product.categoryId) {
      r.push('categoryId');
    } else if (!!localValues.categoriesMap &&
      localValues.categoriesMap[`${product.categoryId}`].criteria === 'WEIGHT' &&
      !product.weight) {
      r.push('weight');
    }
    return r;
  };

  modifyStateAfterScrap(product: ScrappedProduct, preloaded: boolean = false, cb?: () => void) {
    const {localValues, localeState, chosenVariants} = this.props;
    const {shipping, customShipping, formType, pref} = this.state;
    if (this.scrapTimeoutHandle && window) window.clearTimeout(this.scrapTimeoutHandle);
    // Track scrapped product
    const productTrackingInfo = analytics.mapProduct(product, localValues.categoriesMap, 'item_category');
    analytics.fireCustomEvent('Product Scrapped', {
      category: 'Product',
      action: 'Scrapped',
      user_type: 'purchaser',
      user_referrer: window.localStorage.getItem('userReferrer') || '',
      landing_url: window.localStorage.getItem('landingUrl') || '',
      domain_country: localeState.domainCountry,
      ...productTrackingInfo,
    }, {
      integrations: {
        'All': false,
        'Google Analytics': false,
        'Amplitude': false,
        'Facebook Pixel': true,
      },
    });
    // TODO: US is hardcoded for now
    /*if (!departureCountry.value) {
      this.setState({
        departureCountry: {
          value: product.country,
        },
      });
    } else if (!product.country && !departureCountry.value) {
      this.setState({
        departureCountry: {
          value: '',
          errors: ['countryNotScrapped'],
        },
      });
    }*/
    this.lastScrappedProduct = {...product};

    const updatedProductFields: { [key: string]: any } = {};
    const unscrappedFields = this.getUnscrappedFields(product);
    unscrappedFields.forEach((f: string) => {
      updatedProductFields[f] = {errors: {$set: ['unscrapped']}};
    });
    this.setState((prevState: State) => update(prevState, updatedProductFields));

    let decodedUrl = product.url;
    let decodedName = product.name;
    let decodedDescription = product.detail;
    let decodedAmount = parseInt(product.amount, 10);

    try {
      decodedUrl = decodeURIComponent(product.url || '');
    } catch (e) {
    }

    try {
      decodedName = decodeURIComponent(product.name || '');
    } catch (e) {
    }

    try {
      decodedDescription = decodeURIComponent(product.detail || '');
    } catch (e) {
    }

    const up: { [key: string]: any } = {
      url: {value: {$set: decodedUrl}},
      name: {value: {$set: decodedName}},
      shipping: {value: {$set: product.shipping || ''}},
      price: {value: {$set: product.price || ''}},
      description: {value: {$set: decodedDescription}},
      onlineStore: {$set: product.onlineStore || {}},
      amount: {value: {$set: !isNaN(decodedAmount) ? decodedAmount : 1}},
      // preferredImportationMethod: {$set: isIphone13(product.url, product.name) ? 'lite' : ''},
      // preferredImportationMethod: {$set: ''},
    };

    if (!!this.priceCleave && !!this.priceCleave.element) {
      this.priceCleave.setRawValue(product.price || '');
    }
    const parsedShipping = parseFloat(shipping.value);
    const parsedProductShipping = parseFloat(product.shipping);

    if (!!shipping.value && !!product.shipping) {
      up.customShipping = {$set: true};
      this.shippingCleave.setRawValue(product.shipping || '');
      up.shipping = {value: {$set: product.shipping || ''}};
    } else if (!!shipping.value && !isNaN(parsedShipping) && !!product.shipping && !isNaN(parsedProductShipping)) {
      if (parsedShipping < parsedProductShipping) {
        this.shippingCleave.setRawValue(product.shipping || '');
        up.shipping = {value: {$set: product.shipping || ''}};
        up.customShipping = {$set: false};
      } else if (customShipping) {
        up.shipping = {value: {$set: shipping.value || ''}};
      }
    }

    if (typeof product.categoryId === 'number') {
      up.categoryId = {
        value: {$set: `${product.categoryId}`},
      };
      this.updateFormValidator(product.categoryId);
    }

    if (product.volumeTier) {
      // If categoriesMap exists and product has a valid categoryId and correct criteria
      if (!!localValues && !!localValues.categoriesMap &&
        localValues.categoriesMap[`${product.categoryId}`] &&
        localValues.categoriesMap[`${product.categoryId}`].criteria === 'VOLUME') {
        const foundIndex = localValues.categoriesMap[`${product.categoryId}`]
          .volumeTiers.filter(x => x.enabled).findIndex(x => x.code === product.volumeTier);
        if (foundIndex !== -1) {
          up.volumeTier = {
            value: {$set: foundIndex},
            code: {$set: product.volumeTier},
          };
        }
      }
    }

    if (product.weight) {
      const weightValue = parseFloat(product.weight);

      if (/*!!localValues && !!localValues.categoriesMap && typeof product.categoryId === 'number' &&
        localValues.categoriesMap[`${product.categoryId}`].criteria === 'WEIGHT' && */!isNaN(weightValue)) {

        const weightValueStr = (Math.round(weightValue * 100) / 100).toFixed(2);

        if (!!this.weightCleave && !!this.weightCleave.element) {
          this.weightCleave.setRawValue(weightValueStr);
        }

        up.weight = {
          value: {$set: weightValueStr},
          valueKg: {$set: weightValueStr},
        };
      }
    }

    if (product.image) {
      const imageUrl = decodeURIComponent(product.image);
      const imageValue = product.image.startsWith('//') ? `http:${imageUrl}` : imageUrl;
      up.image = {
        value: {$set: imageValue},
      };
    }

    if (!!product.onlineStore) {
      up.onlineStore = {$set: product.onlineStore};
    }

    // console.log('product.originalPackaging', product.originalPackaging);
    if (typeof product.originalPackaging === 'boolean') {
      if (!!localValues && !!localValues.categoriesMap && typeof product.categoryId === 'number' &&
        localValues.categoriesMap[product.categoryId].originalPackagingSelectable) {
        up.withBox = {
          // value: {$set: product.originalPackaging === true ? 1 : 0},
          value: {$set: true},
        };
      }
    }

    if (product.vendorData) {
      up.vendorData = {
        $set: product.vendorData,
      };
    }

    let shouldUpdateDetail = false;

    // If there's some pre selected detail option
    if (!!product.detailOptions) {
      if (!!product.detailOptions.preselected) {
        const auxUp = {};
        // up.detailOptions = {};
        console.log('!!!! product.detailOptions.preselected', product.detailOptions.preselected);
        Object.keys(product.detailOptions.preselected).forEach((k: string) => {
          auxUp[k] = {
            value: product.detailOptions.preselected[k],
            disabled: {},
          };
        });
        up.detailOptions = {
          $set: auxUp,
        };
        const s = this.getDataFromSelectedDetailOption(product.detailOptions.preselected);
        // Set up price, name and image
        if (!!s) {
          shouldUpdateDetail = true;
          // We don't actually need to change the name, options are going to be included in details
          /*if (s.name) {
            up.name = {value: {$set: s.name}};
          }*/
          if (s.image) {
            up.image = {value: {$set: s.image}};
          }
          if (s.url) {
            up.url = {value: {$set: s.url}};
          }
          if (s.price) {
            up.price = {value: {$set: s.price}, $unset: ['errors']};
          }
          const weightValue = parseFloat(s.weight);

          if (s.weight && !isNaN(weightValue)) {
            const weightValueStr = (Math.round(weightValue * 100) / 100).toFixed(2);

            if (!!this.weightCleave && !!this.weightCleave.element) {
              this.weightCleave.setRawValue(weightValueStr);
            }
            up.weight = {
              value: {$set: weightValueStr},
              valueKg: {$set: weightValueStr},
              $unset: ['errors'],
            };
          }
        }
      } else {
        const dimensions = this.getDimensions();
        const auxUp = {};
        dimensions.forEach((dim: string) => {
          auxUp[dim] = {
            value: '',
            disabled: {},
          };
        });
        up.detailOptions = {
          $set: auxUp,
        };
      }
    }

    // Check if apple tablet only for scrapped
    if (!preloaded) {
      // check if apple
      if (product.onlineStore && product.onlineStore.id === 'ST08333814') {
        if (product.categoryId === 11) {
          delete up.price;
          if (!!this.priceCleave && !!this.priceCleave.element) {
            this.priceCleave.setRawValue('');
          }
        }
      }
    }
    
    // Check if airpods (problem: amazon shut us down)
    if (product.url.startsWith('https://www.amazon.com/') &&
      (product.url.includes('B0D1XD1ZV3') || product.url.includes('B0CRDCXRK2'))
      /*product.name.toLowerCase().includes('airpods pro (2nd generation)')*/) {
      this.setState({notAvailableModal: true});
    }

    this.setState((prevState: State) => update(prevState, up), () => {
      this.changesSubject$.next();
      if (typeof cb === 'function') {
        cb();
      }
      if (shouldUpdateDetail) {
        this.calculateDisableDetailOptions();
        this.changeDescriptionFromDetailOptions();
      }
    });
  }

  // Returns if form is valid
  validateForm = (): boolean => {
    const {localValues, scrappedProduct} = this.props;
    const {
      arrivalCountry, departureCountry, url, image, name,
      price, categoryId, weight, volumeTier, detailOptions, formType,
      quoteMethodType, quoteLiteSubtype, quoteFullSubtype,
    } = this.state;
    let resp = true;
    let fieldToScroll = '';

    const productForm: { [key: string]: ?string } = {
      arrivalCountry: arrivalCountry.value,
      departureCountry: departureCountry.value,
      url: url.value,
      name: name.value,
      image: !!image.value ? image.value : image.valueURI,
      price: price.value,
      categoryId: categoryId.value,
      weight: `${weight.value}`,
      volumeTier: typeof volumeTier.value === 'number' ? `${volumeTier.value}` : '',
    };

    console.log('productForm', productForm);

    const productErrors = this.formValidator.validateForm(productForm);

    console.log('productErrors', productErrors);

    if (productErrors.length) {
      resp = false;
      productErrors.forEach((error: FormValidationResult) => {
          this.setState((st: State) => update(st, {
            [error.field]: {
              errors: {$set: error.errors},
            },
          }));
        },
      );
      fieldToScroll = productErrors[0].field;
    }
    if (!!scrappedProduct && !!scrappedProduct.detailOptions) {
      const dimensions = this.getDimensions();
      let fts;
      dimensions.forEach((d: string) => {
        const errors = FormValidator.validateOnTheFly({
          required: 'required',
        }, detailOptions[d] ? detailOptions[d].value : '');
        if (errors.length) {
          fts = fts || d;
          resp = false;
          this.setState((st: State) => update(st, {
            detailOptions: {
              [d]: {
                $set: {
                  value: st.detailOptions[d] ? st.detailOptions[d].value : '',
                  errors,
                  disabled: {},
                },
              },
            },
          }));
        }
        fieldToScroll = fts || fieldToScroll;
        /*if (detailOptions[d] && detailOptions[d].value) {

        }else {

        }*/
        // scrappedProduct.detailOptions
      });
    }

    if (!fieldToScroll) {
      if (!quoteMethodType && (!quoteFullSubtype || !quoteLiteSubtype)) {
        resp = false;
        fieldToScroll = 'quote';
      }
    }

    if (!!fieldToScroll) {
      scroller.scrollTo(fieldToScroll, SCROLL_OPTIONS);
    }
    return resp;
  };

  updateFormValidator = (categoryId: number) => {
    const {localValues} = this.props;
    if (!!localValues && !!localValues.categoriesMap &&
      localValues.categoriesMap[`${categoryId}`].criteria === 'WEIGHT') {
      this.formValidator.replaceFieldValidators({
        weight: {
          required: (val: string) => {
            if (!val) return true;
            return parseFloat(val) === 0;
          },
        },
      });
    } else {
      this.formValidator.replaceFieldValidators({
        weight: (null: any),
      });
    }
    if (!!localValues && !!localValues.categoriesMap &&
      localValues.categoriesMap[`${categoryId}`].criteria === 'VOLUME') {
      this.formValidator.replaceFieldValidators({
        volumeTier: {
          required: 'required',
        },
      });
    } else {
      this.formValidator.replaceFieldValidators({
        volumeTier: (null: any),
      });
    }
  };

  /*  handleClickRefresh = (e: Event) => {
      const { url } = this.state;
      const { resetAlertErrorState } = this.props;
      if (!url.errors && url.value
        /!*&& product.lastURLScrapped !== product.url.value*!/) {
        resetAlertErrorState();
        this.scrapProduct(url.value);
      }
    };*/

  handleFocusInputText = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      const {weight} = this.state;
      if (name === 'url') {
        e.target.select();
      }
      if (name === 'weight') {
        this.setState({weight: {value: weight.value, valueKg: weight.valueKg}});
      } else {
        this.setState({[name]: {value}});
      }
    }
  };

  handleDeleteImage = () => {
    // We do it outside on purpose because we want to register the section at the time executed.
    this.setState({
      image: {
        valueURI: '',
        value: '',
      },
    });
  };

  handleChangeImage = (acceptedFiles: File[]) => {
    if (acceptedFiles.length) {
      const reader = new FileReader();
      reader.onload = () => {
        const fileAsDataURL = reader.result;
        this.setState({
          image: {
            valueURI: fileAsDataURL.toString(),
            value: '',
          },
        }, () => this.changesSubject$.next());

      };
      reader.onabort = () => console.log('file reading was aborted');
      reader.onerror = () => {
        console.log('error');
        this.setState({
          image: {
            errors: ['maxSize'],
          },
        });
        console.log('file reading has failed');
      };
      reader.readAsDataURL(acceptedFiles[0]);
    }
  };

  handleShippingInit = (cleave: Cleave) => {
    this.shippingCleave = cleave;
  };

  handlePriceInit = (cleave: any) => {
    this.priceCleave = cleave;
  };

  handleWeightInit = (cleave: any) => {
    this.weightCleave = cleave;
  };

  handleChangeCleave = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const {rawValue, name} = e.target;
      const changes = {};
      if (name === 'price') {
        this.priceCleave.setRawValue(rawValue);
        changes[name] = {
          value: rawValue,
        };
      } else if (name === 'shipping') {
        changes[name] = {
          value: rawValue,
        };
        changes.customShipping = true;
        this.shippingCleave.setRawValue(rawValue);
      } else if (name === 'weight' && !!this.weightCleave) {
        const {weightUnit} = this.state;
        this.weightCleave.setRawValue(rawValue);
        let valueKg = '';
        if (weightUnit.value === 'kg') {
          valueKg = rawValue;
        } else if (weightUnit.value === 'lbs') {
          const parsed = parseFloat(rawValue);
          if (!isNaN(parsed)) {
            valueKg = (parsed / KG_TO_LB_FACTOR).toFixed(2);
          }
        }

        changes[name] = {
          value: rawValue,
          valueKg,
        };
      }
      this.setState(changes, () => this.changesSubject$.next());
    }
  };

  handleBlurCleave = (e: Event) => {
    if (e.target instanceof HTMLInputElement) {
      // $FlowIgnoreLine: raw value not in target unless Cleave makes a flowtyped (or the community)
      const {rawValue, name} = e.target;
      this.setState((state: State) => {
        const changes = {};
        let newValue = rawValue;
        if (rawValue) {
          newValue = formatToTwoDecimal(rawValue);
        }
        switch (name) {
          case 'price':
            this.priceCleave.setRawValue(newValue);
            changes[name] = {
              value: newValue,
            };
            break;
          case 'shipping':
            this.shippingCleave.setRawValue(newValue);
            changes[name] = {
              value: newValue,
            };
            break;
          case 'weight':
            this.weightCleave.setRawValue(newValue);
            changes[name] = {
              value: newValue,
              valueKg: formatToTwoDecimal(state.weight.valueKg),
            };
            break;
        }
        return changes;
      });
    }
  };

  handleChangeCategory = (option: Option) => {
    this.setState((state: State) => {
      const {localValues} = this.props;
      const catMap = localValues && localValues.categoriesMap;
      // const volumeTier = {...state.volumeTier};
      const volumeTier = {
        value: undefined,
        code: '',
      };
      // Do not initialize volume tier
      /*if (catMap && catMap[`${option.value}`] && catMap[`${option.value}`].volumeTiers.length !== 0) {
        volumeTier.value = undefined;
        volumeTier.code = 'XS';
      } else {
        volumeTier.value = undefined;
        volumeTier.code = '';
      }*/
      this.updateFormValidator(parseInt(option.value));
      return ({
        categoryId: {
          value: `${option.value}`,
        },
        volumeTier,
      });
    }, () => this.changesSubject$.next());
  };

  handleChangeVolumeTier = (value: number) => {
    const {localValues} = this.props;
    const {categoryId} = this.state;
    const catMap = localValues && localValues.categoriesMap;
    this.setState({
      volumeTier: {
        value,
        code: (catMap && catMap[categoryId.value]) ?
          catMap[`${categoryId.value}`].volumeTiers[value].code : '',
      },
    }, () => this.changesSubject$.next());
  };

  handleChangeAmount = (action: InputQuantityAction) => {
    if (action === 'more') {
      this.setState((state: State) => ({
        amount: {
          value: Math.min(state.amount.value + 1, 5),
        },
      }), () => this.changesSubject$.next());
    } else if (action === 'less') {
      this.setState((state: State) => ({
        amount: {
          value: Math.max(state.amount.value - 1, 1),
        },
      }), () => this.changesSubject$.next());
    }
  };

  handleChangeWeightUnit = (unit: string) => {
    // Note: this only execute when weightUnit.value !== unit controlled by InputWeight.
    const {weight} = this.state;
    const changes = {
      weightUnit: {value: unit},
      weight: {value: '', valueKg: ''},
    };

    if (!!weight.value) {
      let value = '';
      let valueKg = '';
      if (unit === 'lbs') {
        value = (parseFloat(weight.value) * KG_TO_LB_FACTOR).toFixed(2);
        valueKg = weight.value;
      } else if (unit === 'kg') {
        const tv = (parseFloat(weight.value) / KG_TO_LB_FACTOR).toFixed(2);
        value = tv;
        valueKg = tv;
      }
      changes.weight = {
        value,
        valueKg,
      };
    }
    this.setState(changes, () => this.changesSubject$.next());
  };

  handleChangeBoxOption = (withBoxValue: number) => {
    this.setState({
      withBox: {value: withBoxValue},
    }, () => this.changesSubject$.next());
  };

  handleFocusWishList = (e: Event) => {
    const isValid = this.validateForm();
    if (!isValid) {
      console.log('handleFocusWishList error!!');
    } else {
      const {user, authenticated, handleOpenAuthModal} = this.props;
      if (!user || !authenticated) {
        handleOpenAuthModal();
      } else {
        this.setState({hideOptions: false});
      }
    }
  };

  getWishListItem(): WishListItem {
    const {name, url, description, categoryId, weight, price, image, shipping, formType} = this.state;
    const wishListItem: WishListItem = {
      name: name.value,
      url: url.value,
      itemCategoryId: parseInt(categoryId.value),
      price: price.value,
    };
    if (weight.value) {
      wishListItem.weight = weight.value;
    }
    /*
        if (shipping.valueKg) {
          wishListItem.shipping = shipping.value;
        }*/
    if (formType !== 'AMAZON' && !!image) {
      if (image.value) {
        wishListItem.image = image.value;
      }
      if (image.valueURI) {
        wishListItem.imageURI = image.valueURI;
      }
    }
    if (description.value) {
      wishListItem.detail = description.value;
    }
    return wishListItem;
  }

  handleSelectWishList = (option: Option) => {
    const isValid = this.validateForm();
    if (!isValid) {
      console.log('handleSelectWishList error!!');
    } else {
      const {user, authenticated, handleOpenAuthModal} = this.props;
      if (!user || !authenticated) {
        handleOpenAuthModal();
      } else {
        const {postProductToWishList, wishLists} = this.props;
        const wishListOption = `${option ? option.value : ''}`;
        this.setState({modalOpen: true, showProductAdded: false});
        if (wishListOption === 'new') {
          this.setState({wishListOption, wishListName: {value: ''}});
        } else if (wishLists) {
          const index = wishLists.findIndex((wishList: WishList) => wishList.name === wishListOption);
          postProductToWishList(this.getWishListItem(), wishLists[index].nameSlug);
          this.setState({wishListName: {value: wishLists[index].name}});
        }
      }
    }
  };

  handleNativeSelectWishList = (e: Event) => {
    if (e.target instanceof HTMLSelectElement) {
      const option = e.target.value;
      const isValid = this.validateForm();
      if (!isValid) {
        console.log('handleSelectWishList error!!');
      } else {
        const {user, authenticated, handleOpenAuthModal} = this.props;
        this.setState({modalOpen: true, showProductAdded: false});
        if (!user || !authenticated) {
          handleOpenAuthModal();
        } else {
          const {postProductToWishList, wishLists} = this.props;
          const wishListOption = `${option}`;
          if (wishListOption === 'new') {
            this.setState({wishListOption, wishListName: {value: ''}});
          } else if (wishLists) {
            const index = wishLists.findIndex((wishList: WishList) => wishList.name === wishListOption);
            postProductToWishList(this.getWishListItem(), wishLists[index].nameSlug);
            this.setState({wishListName: {value: wishLists[index].name}});
          }
        }
      }
    }
  };

  handleClickAddCart = () => {
    const {isLoadingForecast, handleOpenAuthModal} = this.props;
    const isValid = this.validateForm();
    if (!isValid || isLoadingForecast) {
      console.log('handleClickAddCart error!!');
    } else {
      const {
        user, authenticated, postShoppingCartItem, postVirtualShoppingCartItem, virtualShoppingCart,
        setNewAlert,
      } = this.props;
      const {departureCountry} = this.state;
      if (!user || !authenticated) {
        /*if (virtualShoppingCart && virtualShoppingCart.length < 5) {
          const item = this.buildItemRequest(true);
          const req: VirtualItemRequestApi = {
            purchaseCountry: /!*departureCountry.value*!/'US',
            item,
            shippingRequest: {
              purchaseCountry: /!*departureCountry.value*!/'US',
              items: this.buildShippingRequest(true),
            },
          };
          postVirtualShoppingCartItem(req);
          this.setState({
            lastShoppingCartItemAdded: {
              name: item.name,
              image: item.image || '',
              quoteMethodType: item.serviceTier ? 'FULL' : 'LITE',
              quoteFullSubtype: item.serviceTier,
              quoteLiteSubtype: item.courierId,
            },
          });
          this.resetState(() => {
          }, {formType: {$set: 'DEFAULT'}});
        } else {
          setNewAlert('shopping-cart', 'error', undefined, 'newOrder.product.cart.maxQuantityExceeded');
        }

        scroll.scrollTo(0, {
          duration: 400,
        });*/
        this.nextButtonPressHoldAction = 'addCart';
        handleOpenAuthModal();
      } else {
        const item = this.buildItemRequest(true);
        const req: ShoppingCartRequestApi = {
          purchaseCountry: /*departureCountry.value*/'US',
          shoppingCartItems: [item],
        };
        if (this.originType) {
          req.originType = this.originType;
        }
        this.setState({
          lastShoppingCartItemAdded: {
            name: item.name,
            image: item.image || '',
            quoteMethodType: item.serviceTier ? 'FULL' : 'LITE',
            quoteFullSubtype: item.serviceTier,
            quoteLiteSubtype: item.courierId,
          },
        });
        postShoppingCartItem(req);
        this.resetState(() => {
        }, {formType: {$set: 'DEFAULT'}});
      }
    }
  };

  handleClickBuyNow = () => {
    const {isLoadingForecast, handleOpenAuthModal, checkoutFetchCheckoutDataSuccess, forecast} = this.props;
    const {quoteMethodType, quoteFullSubtype, quoteLiteSubtype, preferredImportationMethod}: State = this.state;
    const isValid = this.validateForm();
    if (!isValid || isLoadingForecast) {
      console.log('handleClickBuyNow error!!');
    } else {
      const {user, authenticated, postBagItem, postVirtualBagItem, deleteVirtualBag} = this.props;
      const {departureCountry} = this.state;
      // TODO: All "Buy Now" is going through virtual bag
      if (!user || !authenticated) {
        /*const bagRequest: VirtualItemRequestApi = {
          purchaseCountry: /!*departureCountry.value*!/'US',
          item: this.buildItemRequest(),
          shippingRequest: {
            purchaseCountry: /!*departureCountry.value*!/'US',
            items: this.buildShippingRequest(),
          },
        };
        deleteVirtualBag();
        let queryParams = {};
        if (this.preferredImportationMethod === 'full' || this.preferredImportationMethod === 'lite') {
          queryParams['preferred_importation_method'] = this.preferredImportationMethod;
        }
        if (this.apds) {
          queryParams['apds'] = this.apds;
        }
        postVirtualBagItem(bagRequest, {...queryParams});*/
        this.nextButtonPressHoldAction = 'buyNow';
        handleOpenAuthModal();
      } else {
        const bagRequest: PostBagRequestApi = {
          country: /*departureCountry.value*/'US',
          items: [this.buildItemRequest()],
        };
        if (this.originType) {
          bagRequest.originType = this.originType;
        }
        let queryParams: {[key: string]: string} = {};
        if (preferredImportationMethod === 'full' || preferredImportationMethod === 'lite') {
          queryParams = { preferred_importation_method: preferredImportationMethod };
        }
        if (this.apds) {
          queryParams['apds'] = this.apds;
        }


        if (forecast && quoteMethodType) {
          let selectedIndex;
          let importMethod;

          if (quoteMethodType === 'FULL') {
            importMethod = 'CLASSIC';
            selectedIndex = forecast['CLASSIC'].findIndex((f) => f.tier.name === quoteFullSubtype);
          }else {
            importMethod = 'DIY';
            selectedIndex = forecast['DIY'].findIndex((f) => f.courier.id === quoteLiteSubtype);
          }
          checkoutFetchCheckoutDataSuccess(forecast, selectedIndex, importMethod);
          postBagItem(bagRequest, undefined, { ...queryParams });
        }

      }
    }
  };

  buildShippingRequest = (isCart?: boolean): ShippingItemRequestApi[] => {
    const {amount, customShipping, url, price, shipping} = this.state;
    const {virtualShoppingCart} = this.props;
    const shippingItemRequest: { [key: string]: any } = {
      amount: amount.value,
      itemId: generateRandomNumber(),
      price: price.value,
      url: url.value,
    };

    if (customShipping) {
      if (shipping.value) {
        shippingItemRequest.shipping = shipping.value;
        shippingItemRequest.isShippingAutomatic = false;
      }
      // If there's an ebay shipping then send it
    } else if (this.lastScrappedProduct && !!this.lastScrappedProduct.shipping) {
      shippingItemRequest.shipping = this.lastScrappedProduct.shipping;
      shippingItemRequest.isShippingAutomatic = false;
    }
    if (isCart) {
      const items = virtualShoppingCart.map((virtualItem: VirtualItem) => {
        const shippingItemRequest: { [key: string]: any } = {
          amount: virtualItem.amount,
          itemId: virtualItem.itemId,
          price: virtualItem.price,
          url: virtualItem.url,
        };
        if (typeof virtualItem.isShippingAutomatic !== 'undefined') {
          shippingItemRequest.shipping = virtualItem.shipping;
          shippingItemRequest.isShippingAutomatic = virtualItem.isShippingAutomatic;
        }
        return shippingItemRequest;
      });
      return [...items, shippingItemRequest];
    } else {
      return [shippingItemRequest];
    }
  };

  buildItemRequest = (includeQuoteData: boolean = false): PostItemRequestApi | ShoppingCartItemRequestApi => {
    const {
      url, name, categoryId, image, description, amount, price, withBox, weight, customShipping, shipping,
      volumeTier, formType, quoteMethodType, quoteFullSubtype, quoteLiteSubtype,
    } = this.state;
    const {localValues, authenticated, user, virtualShoppingCart} = this.props;
    const itemCategoryId = parseInt(categoryId.value, 10);
    const itemRequest: { [key: string]: any } = {
      url: url.value,
      name: name.value,
      itemCategoryId,
      amount: amount.value,
      price: price.value,
    };
    const catMap = localValues && localValues.categoriesMap;
    const categoryCriteria = catMap && catMap[`${itemCategoryId}`] && catMap[`${itemCategoryId}`].criteria;

    if (includeQuoteData) {
      if (quoteMethodType === 'FULL') {
        itemRequest.serviceTier = quoteFullSubtype;
      } else {
        itemRequest.courierId = quoteLiteSubtype;
      }
    }
    if (!!weight.value && categoryCriteria === 'WEIGHT') {
      itemRequest.weight = weight.valueKg;
    }
    if (image.value) {
      itemRequest.image = image.value;
    }
    if (image.valueURI) {
      itemRequest.imageURI = image.valueURI;
    }
    if (description.value) {
      itemRequest.detail = description.value;
    }
    if (localValues.categoriesMap && localValues.categoriesMap[`${itemCategoryId}`].originalPackagingSelectable) {
      // itemRequest.originalPackaging = withBox.value === 1;
      itemRequest.originalPackaging = true;
    }
    if (customShipping) {
      if (shipping.value) {
        itemRequest.shipping = shipping.value;
        if (!authenticated || !user) {
          itemRequest.isShippingAutomatic = false;
        }
      }
    } else if (this.lastScrappedProduct && !!this.lastScrappedProduct.shipping) {
      itemRequest.shipping = this.lastScrappedProduct.shipping;
    }
    if (volumeTier.code && categoryCriteria === 'VOLUME') {
      itemRequest.volumeTier = volumeTier.code;
    }
    if (!user || !authenticated) {
      itemRequest.itemId = generateRandomNumber();
    }
    return itemRequest;
  };

  buildForecastRequest = (): ForecastPricesApiReq | typeof undefined => {
    const {
      arrivalCountry, departureCountry, categoryId, price, image,
      amount, withBox, weight, url, name, volumeTier, shipping,
    } = this.state;
    const { localValues } = this.props;
    const departureCountryErrors = this.formValidator.validateField('departureCountry', departureCountry.value);
    const arrivalCountryErrors = this.formValidator.validateField('arrivalCountry', arrivalCountry.value);
    const priceErrors = this.formValidator.validateField('price', price.value);
    const categoryIdErrors = this.formValidator.validateField('categoryId', categoryId.value);
    const imageErrors = this.formValidator.validateField('image', !!image.value ? image.value : image.valueURI);
    const volumeTierErrors = this.formValidator.validateField(
      'volumeTier',
      typeof volumeTier.value === 'number' ? `${volumeTier.value}` : ''
    );

    if (!localValues.categoriesMap) return;

    if (!departureCountryErrors.length && !arrivalCountryErrors.length && !imageErrors.length &&
      !priceErrors.length && !categoryIdErrors.length && !volumeTierErrors.length) {

      if (localValues.categoriesMap && categoryId.value) {
        if (localValues.categoriesMap[categoryId.value].criteria === 'WEIGHT' && !weight.value) {
          return;
        }
        if (localValues.categoriesMap[categoryId.value].criteria === 'VOLUME' && !volumeTier.code) {
          return;
        }
      }

      const item: { [key: string]: any } = {
        name: name.value || '',
        categoryId: parseInt(categoryId.value, 10),
        price: price.value,
        amount: amount.value,
        // originalPackaging: withBox.value === 1,
        originalPackaging: true,
        shipping: shipping.value || '0.00',
      };

      if (volumeTier.code) item.volumeTier = volumeTier.code;

      if (weight.valueKg) item.weight = weight.valueKg;

      if (url.value) item.url = url.value;

      return {
        purchaseCountry: 'US',
        deliveryCountry: 'PE',
        items: [item],
      };
    }
  };

  handleCloseModal() {
    this.setState({modalOpen: false, showProductAdded: false, wishListOption: ''});
  }

  handleCloseErrorModal() {
    this.setState({showError: false});
  }

  handleNewWishListNameChange(e: Event) {
    if (e.target instanceof HTMLInputElement) {
      const {name, value} = e.target;
      this.setState({[name]: {value}});
    }
  }

  handleClickAddToWishList(e: Event) {
    e.preventDefault();
    const {postNewWishList} = this.props;
    const {wishListName} = this.state;
    if (wishListName.value) {
      this.setState({wishListOption: ''});
      postNewWishList(wishListName.value, this.getWishListItem());
    }
  }

  handleOpenShippingHelpModal = () => {
    this.setState({modalShipping: true});
  };

  handleCloseShippingHelpModal = () => {
    this.setState({modalShipping: false});
  };

  handleGoToWishList(e: Event) {
    e.preventDefault();
  }

  handleContinueBuying(e: Event) {
    e.preventDefault();
    this.resetState(this.handleCloseModal);
  }

  handleDeleteProduct = (e: Event) => {
    e.preventDefault();
    this.resetState(() => {
      this.cancelForecast();
    });
    scroll.scrollTo(0, {
      duration: 400,
    });
  };

  getDimensions = (): string[] => {
    const {scrappedProduct} = this.props;
    if (!scrappedProduct || !scrappedProduct.detailOptions) {
      return [];
    } else {
      return Object.keys(scrappedProduct.detailOptions.variationDimensions);
    }
  };

  getUpdateProductDataObject = (s: ScrappedDetailOptionItem) => {
    const up = {};

    // We don't actually need to change the name, options are going to be included in details
    /*if (s.name) {
      up.name = {value: {$set: s.name}, errors: {$set: undefined}};
    }*/
    if (s.image) {
      up.image = {value: {$set: s.image}, errors: {$set: undefined}};
    } else if (this.lastScrappedProduct) {
      up.image = this.lastScrappedProduct.image;
    }
    if (s.url) {
      up.url = {value: {$set: s.url}, errors: {$set: undefined}};
    }
    if (s.price) {
      up.price = {value: {$set: s.price}, errors: {$set: undefined}};
    }
    if (s.weight) {
      up.weight = {
        value: {$set: s.weight},
        valueKg: {$set: s.weight},
        errors: {$set: undefined},
      };
    }
    return up;
  };

  getDataFromSelectedDetailOption = (picked: { [key: string]: string }): ?ScrappedDetailOptionItem => {
    const {scrappedProduct} = this.props;
    const dimensions = this.getDimensions();
    const kpicked = Object.keys(picked);
    if (kpicked.length !== dimensions.length) {
      return undefined;
    }
    if (scrappedProduct && scrappedProduct.detailOptions) {
      let idx = 0;
      const pickedFirstKey = kpicked[idx];
      const pickedFirstValue = picked[pickedFirstKey];
      let depth = scrappedProduct.detailOptions.variationRouting[pickedFirstKey][pickedFirstValue];
      while (typeof depth !== 'string' || idx < kpicked.length - 1) {
        idx = idx + 1;
        const nPickedKey = kpicked[idx];
        const nPickedValue = picked[nPickedKey];
        if (!!depth) {
          depth = depth[nPickedKey][nPickedValue];
        } else {
          break;
        }
      }
      // console.log('depth', depth);
      if (!depth) {
        // console.log('not found');
        return undefined;
      } else {
        // depth is the code found in variationRouting
        const item = scrappedProduct.detailOptions.variations[depth];
        // console.log('item', item);
        return item;
      }
    }
  };

  changeDescriptionFromDetailOptions = () => {
    const {detailOptions} = this.state;

    const dimensions = this.getDimensions();
    let description = '';

    for (let dim of dimensions) {
      if (!!detailOptions[dim] && !!detailOptions[dim].value) {
        description = `${description}\n${dim}: ${detailOptions[dim].value}`;
      }
    }

    this.setState({
      description: {
        value: description,
      },
    });
  };

  calculateDisableDetailOptions = () => {
    const {detailOptions} = this.state;
    const {scrappedProduct} = this.props;
    const dimensions = this.getDimensions();

    let incomplete = false;

    for (let dim of dimensions) {
      if (!detailOptions[dim] || !detailOptions[dim].value) {
        // console.log('detail options incomplete');
        incomplete = true;
      }
    }

    if (!!scrappedProduct && !!scrappedProduct.detailOptions) {

      if (incomplete) {
        // console.log('incomplete');
        const dimsWithValues = dimensions.filter((dim: string) => !!detailOptions[dim].value);
        // console.log('dimsWithValues', dimsWithValues);
        let depth = undefined;
        for (let dv of dimsWithValues) {
          depth = !!depth ? depth[dv][detailOptions[dv].value] :
            scrappedProduct.detailOptions.variationRouting[dv][detailOptions[dv].value];
          // console.log('depth', depth);
        }
        // console.log('Finaldepth', depth);
        const dimsWithoutValues = dimensions.filter((dim: string) => !dimsWithValues.includes(dim));
        // console.log('dimsWithoutValues', dimsWithoutValues);


        let up = {
          detailOptions: {},
        };
        for (let dwv of dimsWithoutValues) {
          (scrappedProduct: ScrappedProduct).detailOptions.variationDimensions[dwv]
            .forEach((val: string) => {
              if (!!depth) {
                // console.log('dwv', dwv);
                // console.log('depth[dwv]', depth[dwv]);
                const validValues = Object.keys(depth[dwv]);
                // console.log(dmw, validValues);
                // console.log('validValues', validValues);
                // console.log('val', val, !validValues.includes(val));
                const s = !validValues.includes(val);
                // console.log('!!this.state.detailOptions[dwv].disabled', !!this.state.detailOptions[dwv].disabled);
                const disabledUpdate = {[val]: {$set: s}};
                // console.log('disabledUpdate', disabledUpdate);

                const previousDetail = !!up.detailOptions[dwv] ? up.detailOptions[dwv].disabled : {};
                up.detailOptions[dwv] = {
                  disabled: {...previousDetail, ...disabledUpdate},
                };
              }
            });
          // console.log('-----------');
        }
        this.setState((st: State) => update(st, up));
      } else {
        // console.log('complete');
        for (let dim of dimensions) {
          const as = this.getDimensions().filter((d: string) => d !== dim);
          let d;
          for (let a of as) {
            d = !!d ?
              d[a][detailOptions[a].value] : scrappedProduct.detailOptions.variationRouting[a][detailOptions[a].value];
            // console.log('=>', dim, d);
          }
          if (!!d) {
            let up = {
              detailOptions: {},
            };
            const validValues = Object.keys(d[dim]);

            (scrappedProduct: ScrappedProduct).detailOptions.variationDimensions[dim]
              .forEach((val: string) => {
                const s = !validValues.includes(val);

                const disabledUpdate = {[val]: {$set: s}};
                // console.log('disabledUpdate', disabledUpdate);

                const previousDetail = !!up.detailOptions[dim] ? up.detailOptions[dim].disabled : {};

                up.detailOptions[dim] = {
                  disabled: {...previousDetail, ...disabledUpdate},
                };
              });
            // console.log(dim, validValues, '-------------');
            // console.log('this.state.detailOptions', this.state.detailOptions);
            // console.log('up', up);
            this.setState((st: State) => update(st, up));
          }
        }
      }
    }


    /*const { scrappedProduct } = this.props;
    const { detailOptions } = this.state;
    if (!!scrappedProduct) {
      let dimensions = this.getDimensions().filter((d: string) => d !== startDimension);
      const kdopts = Object.keys(detailOptions);
      console.log('dimensions', dimensions);
      kdopts.forEach((k: string) => {

      });
      dimensions.forEach((d: string) => {
        scrappedProduct.variationRouting[];
      });
    }*/
  };

  pickFirstValidDetailOption = (dimension: string, value: string, cb?: () => void) => {
    const {scrappedProduct} = this.props;
    const up = {
      detailOptions: {},
    };
    let depth = (scrappedProduct: ScrappedProduct).detailOptions.variationRouting[dimension][value];
    while (typeof depth === 'object') {
      const firstDim = Object.keys(depth)[0];
      depth = depth[firstDim];
      const firstValue = Object.keys(depth)[0];
      depth = depth[firstValue];
      up.detailOptions[firstDim] = {
        value: {$set: firstValue},
      };
    }
    this.setState((st: State) => update(st, up), () => {
      const h = {...this.state.detailOptions};
      Object.keys(h).forEach((k: string) => h[k].value ? h[k] = h[k].value : null);
      // console.log('h', h);
      const s = this.getDataFromSelectedDetailOption(h);
      const upd = this.getUpdateProductDataObject(s);
      upd.detailOptions = {};
      if (s) {
        upd.vendorData = {$set: s.vendorData};
      }
      this.setState((sta: State) => update(sta, upd), () => {
        if (!!cb) {
          cb();
        }
      });
    });
  };

  getVariantFromStateDetailOption = (): ?ScrappedDetailOptionItem => {
    const {detailOptions} = this.state;
    const h = {...detailOptions};
    Object.keys(h).forEach((k: string) => h[k].value ? h[k] = h[k].value : null);
    return this.getDataFromSelectedDetailOption(h);
  };

  handleChangeDetailOption = (dimension: string, value: string) => {
    this.setState((st: State) => update(st, {
      detailOptions: {
        [dimension]: {$set: {value, disabled: {}}},
      },
    }), () => {
      const {detailOptions} = this.state;
      const s = this.getVariantFromStateDetailOption();
      if (!!s) {
        const up = this.getUpdateProductDataObject(s);
        up.vendorData = {$set: s.vendorData};

        // console.log('up', up);
        // Delete disabled
        const upDetailOptions = {};
        Object.keys(detailOptions).forEach((k: string) => {
          upDetailOptions[k] = {
            value: detailOptions[k].value,
            disabled: {},
          };
        });
        up.detailOptions = {
          $set: upDetailOptions,
        };
        this.setState((st: State) => update(st, up),
          () => {
            this.calculateDisableDetailOptions();
            this.changeDescriptionFromDetailOptions();
            this.changesSubject$.next();
            this.priceCleave.setRawValue(this.state.price.value);
          });
      } else {
        const dimensions = this.getDimensions();

        const up = {
          vendorData: {$set: {}},
        };

        for (let dim of dimensions) {
          if (!detailOptions[dim] || !detailOptions[dim].value) {
            // console.log('detail options incomplete');
            this.calculateDisableDetailOptions();
            return;
          }
        }

        this.pickFirstValidDetailOption(dimension, value, () => this.calculateDisableDetailOptions())
        /*const up = {
          detailOptions: {},
        };
        dimensions.forEach((d: string) => {
          if (d !== dimension) {
            up.detailOptions[d] = { $set: { value: '', disabled: {} } };
          }
        });
        this.setState((st: State) => update(st, up),
          () => this.calculateDisableDetailOptions());*/

        this.setState((st: State) => update(st, up));
      }
    });
  };

  cancelForecast = () => {
    if (this.cancelableForecast) {
      this.cancelableForecast.cancel();
    }
  };

  resetState = (callback?: Function, args?: any) => {
    const {scrapResetState} = this.props;
    this.lastScrappedProduct = undefined;
    scrapResetState(); // callback below might set a loading state inside scrap state
    this.setState((prevState: State) => update(prevState, {
      $merge: DEFAULT_EMPTY_ITEM,
      ...args,
      forecast: {$set: undefined},
    }), callback);
    if (!!this.priceCleave && !!this.priceCleave.element) {
      this.priceCleave.setRawValue('');
    }
    if (!!this.shippingCleave && !!this.shippingCleave.element) {
      this.shippingCleave.setRawValue('');
    }
  };

  handleChangeMethod = ({type, subtype}: { type: ForecastType; subtype: string }) => {
    const s: { [key: string]: string } = {
      quoteMethodType: type,
    };

    if (type === 'LITE') s.quoteLiteSubtype = subtype;
    if (type === 'FULL') s.quoteFullSubtype = subtype;

    this.setState(s);
  };

  render() {
    const {
      arrivalCountry, departureCountry, url, image, name, description, price, categoryId, amount, weightUnit,
      weight, withBox, loadingForecast, modalOpen, wishListOption, wishListName, showProductAdded,
      hideOptions, showError, onlineStore, modalShipping, volumeTier, shipping, couldNotScrap,
      isScrappingModalShouldAppear, showSlider, detailOptions, formType, vendorData,
      quoteMethodType, quoteFullSubtype, quoteLiteSubtype, shoppingCartItemAddedModal, lastShoppingCartItemAdded,
      preferredImportationMethod, notAvailableModal,
    } = this.state;
    const {
      localValues, wishLists, isPostingProduct, isPostingNewWishList, isPostingBag, isPostingCartItem,
      localeState, chosenVariants, isScrappingUrl, scrappedProduct, location,
      forecast, isLoadingForecast,
    } = this.props;

    const countryOptions: Option[] = (localValues.countries || DEFAULT_COUNTRIES).map((country) => ({
      value: country.key,
      label: getLocalizedCountry(country.key, this.context.intl.formatMessage, localValues.countriesMap),
      image: country.icon,
    }));

    const sliderSettings = {
      infinite: true,
      autoplay: true,
      autoplaySpeed: 4000,
      dots: false,
      arrows: false,
      draggable: false,
    };

    const advicesData = [
      {
        img: scrapAdvice1,
        intl: 'newOrder.product.modal.loadingScrap.advice.1',
      },
      {
        img: scrapAdvice2,
        intl: 'newOrder.product.modal.loadingScrap.advice.2',
      },
      {
        img: scrapAdvice3,
        intl: 'newOrder.product.modal.loadingScrap.advice.3',
      },
      {
        img: scrapAdvice4,
        intl: 'newOrder.product.modal.loadingScrap.advice.4',
      },
    ];

    const advices = advicesData.map(({img, intl}, idx: number) =>
      <div key={idx} className={style.advice}>
        <div className={style.imageWrap}><img src={img} alt="advice"/></div>
        <FormattedMessage id={intl}>{
          txt => <p>{txt}</p>
        }</FormattedMessage>
      </div>);

    const forecastRequest = this.buildForecastRequest();

    let productForm;
    /*let productForm = (
      <ProductForm handleChangeInputText={this.handleChangeInputText}
                   handleChangeInputUrl={this.handleChangeInputUrl}
                   handleFocusInputText={this.handleFocusInputText}
                   handleBlurCleave={this.handleBlurCleave}
        // handleClickRefresh={this.handleClickRefresh}
                   handleDeleteImage={this.handleDeleteImage}
                   handleChangeImage={this.handleChangeImage}
                   handleShippingInit={this.handleShippingInit}
                   handlePriceInit={this.handlePriceInit}
                   handleWeightInit={this.handleWeightInit}
                   handleChangeCleave={this.handleChangeCleave}
                   handleChangeCategory={this.handleChangeCategory}
                   handleChangeAmount={this.handleChangeAmount}
                   handleChangeWeightUnit={this.handleChangeWeightUnit}
                   handleChangeVolume={this.handleChangeVolumeTier}
                   handleChangeBoxOption={this.handleChangeBoxOption}
                   handleFocusWishList={this.handleFocusWishList}
                   handleClickAddCart={this.handleClickAddCart}
                   handleClickBuyNow={this.handleClickBuyNow}
                   handleOpenShippingHelpModal={this.handleOpenShippingHelpModal}
                   handleSelectWishList={this.handleSelectWishList}
                   handleNativeSelectWishList={this.handleNativeSelectWishList}
                   handleDeleteProduct={this.handleDeleteProduct}
                   handleOnKeyPress={this.handleOnKeyPress}
                   categories={localValues.categories}
                   localeState={localeState}
                   localValues={localValues}
                   departureCountry={departureCountry}
                   url={url}
                   image={image}
                   name={name}
                   description={description}
                   price={price}
                   categoryId={categoryId}
                   amount={amount}
                   weightUnit={weightUnit}
                   weight={weight}
                   volumeTier={volumeTier}
                   withBox={withBox}
                   isScrapping={isScrappingUrl || !localValues.categoriesMap}
                   loadingForecast={loadingForecast}
                   forecast={forecast}
                   wishLists={wishLists}
                   hideOptions={hideOptions}
                   shipping={shipping}
                   onlineStore={onlineStore}
                   scrappedProduct={scrappedProduct}
                   handleChangeDetailOption={this.handleChangeDetailOption}
                   detailOptions={detailOptions}
                   preferredImportationMethod={this.preferredImportationMethod}/>
    );

    if (formType === 'AMAZON') {
      productForm = (
        <AmazonProductForm handleFocusInputText={this.handleFocusInputText}
                           handleBlurCleave={this.handleBlurCleave}
                           handleWeightInit={this.handleWeightInit}
                           handlePriceInit={this.handlePriceInit}
                           handleChangeCleave={this.handleChangeCleave}
                           handleChangeCategory={this.handleChangeCategory}
                           handleChangeAmount={this.handleChangeAmount}
                           handleChangeWeightUnit={this.handleChangeWeightUnit}
                           handleChangeVolume={this.handleChangeVolumeTier}
                           handleChangeBoxOption={this.handleChangeBoxOption}
                           handleClickAddCart={this.handleClickAddCart}
                           handleClickBuyNow={this.handleClickBuyNow}
                           categories={localValues.categories}
                           locale={localeState}
                           localValues={localValues}
                           location={location}
                           departureCountry={departureCountry}
                           name={name}
                           image={image}
                           description={description}
                           shipping={shipping.value}
                           price={price}
                           categoryId={categoryId}
                           amount={amount}
                           weightUnit={weightUnit}
                           weight={weight}
                           volumeTier={volumeTier}
                           withBox={withBox}
                           isScrapping={isScrappingUrl || !localValues.categoriesMap}
                           hideOptions={hideOptions}
                           onlineStore={onlineStore}
                           scrappedProduct={scrappedProduct}
                           handleChangeDetailOption={this.handleChangeDetailOption}
                           getVariantFromStateDetailOption={this.getVariantFromStateDetailOption}
                           detailOptions={detailOptions}
                           vendorData={vendorData}/>
      );
    }*/

    productForm = (
      <NewProductForm handleChangeMethod={this.handleChangeMethod}
                      quoteMethodType={quoteMethodType}
                      quoteFullSubtype={quoteFullSubtype}
                      quoteLiteSubtype={quoteLiteSubtype}
                      forecast={forecast}
                      isLoadingForecast={isLoadingForecast}
                      forecastRequest={forecastRequest}
                      handleChangeInputText={this.handleChangeInputText}
                      handleChangeInputUrl={this.handleChangeInputUrl}
                      handleOnKeyPress={this.handleOnKeyPress}
                      handleFocusInputText={this.handleFocusInputText}
                      handleBlurCleave={this.handleBlurCleave}
                      handleDeleteImage={this.handleDeleteImage}
                      handleChangeImage={this.handleChangeImage}
                      handleWeightInit={this.handleWeightInit}
                      handlePriceInit={this.handlePriceInit}
                      handleShippingInit={this.handleShippingInit}
                      handleOpenShippingHelpModal={this.handleOpenShippingHelpModal}
                      handleChangeCleave={this.handleChangeCleave}
                      handleChangeCategory={this.handleChangeCategory}
                      handleChangeAmount={this.handleChangeAmount}
                      handleChangeWeightUnit={this.handleChangeWeightUnit}
                      handleChangeVolume={this.handleChangeVolumeTier}
                      handleChangeBoxOption={this.handleChangeBoxOption}
                      handleClickAddCart={this.handleClickAddCart}
                      handleClickBuyNow={this.handleClickBuyNow}
                      categories={localValues.categories}
                      locale={localeState}
                      localValues={localValues}
                      location={location}
                      departureCountry={departureCountry}
                      url={url}
                      image={image}
                      name={name}
                      description={description}
                      shipping={shipping.value}
                      price={price}
                      categoryId={categoryId}
                      amount={amount}
                      weightUnit={weightUnit}
                      weight={weight}
                      volumeTier={volumeTier}
                      withBox={withBox}
                      isScrapping={isScrappingUrl || !localValues.categoriesMap}
                      hideOptions={hideOptions}
                      onlineStore={onlineStore}
                      scrappedProduct={scrappedProduct}
                      handleChangeDetailOption={this.handleChangeDetailOption}
                      getVariantFromStateDetailOption={this.getVariantFromStateDetailOption}
                      detailOptions={detailOptions}
                      vendorData={vendorData}
                      preferredImportationMethod={preferredImportationMethod} />
    );

    return (
      <Fragment>
        <UntilTablet>
          <Helmet>
            <style type="text/css">{`
              #chat-bot-launcher-container {
                display: none!important;
              }            
            `}</style>
          </Helmet>
        </UntilTablet>
        <section className={style.productStepWrap}>
          <VerificationBanner />
          <div className={style.productStepContent}>
            {productForm}
          </div>
        </section>
        <Modal open={modalOpen}
               handleClose={this.handleCloseModal.bind(this)}
               width={544}
               height={352}
               closeOnEsc>
          {wishListOption === 'new' &&
          <Fragment>
            <FormattedMessage id="newOrder.product.form.wishList.new">{
              txt => <p className={style.modalTitle}>{txt}</p>
            }</FormattedMessage>
            <form className={classNames(formStyle.formWrap, style.formWrap)}>
              <section className={classNames(formStyle.row, formStyle.inputRow)}>
                <article className={formStyle.col}>
                  <FormattedMessage id="newOrder.product.form.wishList.label">{
                    txt => <label className={formStyle.label}>{txt}</label>
                  }</FormattedMessage>
                  <input name="wishListName"
                         className={classNames(inputStyle.input, {[inputStyle.red]: wishListName.errors && wishListName.errors.length},
                           {[inputStyle.lightBlue]: !!wishListName.value})}
                         value={wishListName.value}
                         onChange={this.handleNewWishListNameChange.bind(this)}/>
                </article>
              </section>
              <FormattedMessage id="newOrder.product.form.wishList.add">{
                txt => <button type="button"
                               className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                               onClick={this.handleClickAddToWishList.bind(this)}
                               disabled={wishListName.value === ''}>{txt}</button>
              }</FormattedMessage>
            </form>
          </Fragment>}
          {(isPostingProduct || isPostingNewWishList) && wishListOption !== 'new' &&
          <Loading type='sk-three-bounce'
                   className={style.modalLoading}
                   number={3}/>}
          {showProductAdded && wishListOption !== 'new' &&
          <section className={style.productAddedWrap}>
            <FormattedMessage id="newOrder.product.modal.wishList.title">{
              txt => <p className={style.title}>{txt} {wishListName.value}</p>
            }</FormattedMessage>
            <p className={style.name}>{name.value}</p>
            <section className={style.middleWrap}>
              {!!image &&
              <img src={image.value ? image.value : image.valueURI}
                   alt={name.value}/>}
              <p className={style.price}>$ {price.value}</p>
              <p className={style.description}>{description.value}</p>
            </section>
            <section className={style.buttonsWrap}>
              <FormattedMessage id="newOrder.product.modal.wishList.seeWishList">{
                txt => <button type="button"
                               className={classNames(buttonStyle.btn, buttonStyle.greyInverse)}
                               onClick={this.handleGoToWishList.bind(this)}>{txt}</button>
              }</FormattedMessage>
              <FormattedMessage id="newOrder.product.modal.wishList.continueBuying">{
                txt => <button type="button"
                               className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                               onClick={this.handleContinueBuying.bind(this)}>{txt}</button>
              }</FormattedMessage>
            </section>
          </section>}
        </Modal>
        <SuccessErrorModal isOpen={showError}
                           handleCloseModal={this.handleCloseErrorModal.bind(this)}
                           titleInt='httpErrors.post.400'
                           color='lightBlue'
                           success={true}/>
        <SuccessErrorModal isOpen={notAvailableModal}
                           handleCloseModal={() => this.setState({notAvailableModal: false})}
                           title="Se ha llegado al límite de pedidos de este producto. Comunícate con nosotros para poder ayudarte a solicitarlo."
                           color='lightBlue'
                           error={true}/>
        <Modal handleClose={() => !isScrappingUrl &&
          this.setState({
            couldNotScrap: false,
            isScrappingModalShouldAppear: false,
            showSlider: false,
          })}
               open={isScrappingModalShouldAppear && isScrappingUrl}
               transparent
               maskColor="white"
               showCloseButton={false}
               big>
          {isScrappingUrl &&
          <div className={style.loadingScrapWrap}>
            <FormattedMessage id="newOrder.product.modal.loadingScrap.title">{
              txt => <h3 className={style.title}>{txt}</h3>
            }</FormattedMessage>
            <img className={style.loadingAPNG}
                 src={loadingAPNG}
                 alt="loading"/>
            {/*<div className={style.advicesWrap}>
              {showSlider && <Slider {...sliderSettings}>{advices}</Slider>}
            </div>*/}
            {/*<div className={style.advicesWrap}>
                <Default><Slider {...sliderSettings}>{advices}</Slider></Default>
                <Mobile><Slider {...sliderSettings}>{advices}</Slider></Mobile>
              </div>*/}
          </div>}
          {/*!isScrappingUrl && couldNotScrap &&
          <section className={style.couldNotScrapModal}>
            <img src={yogi_sad} alt="yogi"/>
            <FormattedMessage id="newOrder.product.modal.couldNotScrap.title">{
              txt => <h3>{txt}</h3>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.product.modal.couldNotScrap.text">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.product.modal.couldNotScrap.button">{
              txt => <button className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                             onClick={() => this.setState({
                               couldNotScrap: false,
                               isScrappingModalShouldAppear: false,
                               showSlider: false,
                             })}>{txt}</button>
            }</FormattedMessage>
          </section>*/}
        </Modal>
        <Modal handleClose={() => undefined}
               open={isPostingBag || isPostingCartItem}
               transparent
               maskColor="white"
               showCloseButton={false}>
          <Loading type="circle"
                   className={style.loading}/>
        </Modal>
        <Modal handleClose={this.handleCloseShippingHelpModal}
               open={modalShipping}
               big>
          <section className={style.shippingHelpModal}>
            <FormattedMessage id="newOrder.product.modal.shippingHelp.title">{
              txt => <h3>{txt}</h3>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.product.modal.shippingHelp.paragraph.one">{
              txt => <p className={style.pOne}>{txt}</p>
            }</FormattedMessage>
            <FormattedMessage id="newOrder.product.modal.shippingHelp.paragraph.two">{
              txt => <p>{txt}</p>
            }</FormattedMessage>
            <section className={classNames(style.addressWrap, formStyle.formWrap)}>
              <section className={formStyle.row}>
                <article className={formStyle.col}>
                  <FormattedMessage id="address.address.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.address">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
              </section>
              <section className={classNames(formStyle.row, formStyle.dualWrap)}>
                <article className={formStyle.col}>
                  <FormattedMessage id="address.country.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.country">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
                <article className={formStyle.col}>
                  <FormattedMessage id="address.admin1.state.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.state">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
              </section>
              <section className={classNames(formStyle.row, formStyle.dualWrap)}>
                <article className={formStyle.col}>
                  <FormattedMessage id="address.admin2.city.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.city">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
                <article className={formStyle.col}>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.zipCode.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.zipCode">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
              </section>
              <section className={classNames(formStyle.row, formStyle.rowNoMargin)}>
                <article className={formStyle.col}>
                  <FormattedMessage id="address.phone.label">{
                    txt => <label className={formStyle.label}>{txt}:</label>
                  }</FormattedMessage>
                  <FormattedMessage id="newOrder.product.modal.shippingHelp.phone">{
                    txt => <p className={style.address}>{txt}</p>
                  }</FormattedMessage>
                </article>
              </section>
            </section>
            <FormattedMessage id="newOrder.product.modal.shippingHelp.extra">{
              txt => <p className={style.extra}>{txt}</p>
            }</FormattedMessage>
          </section>
        </Modal>
        {lastShoppingCartItemAdded && (
          <Modal key="summaryModal"
                 open={shoppingCartItemAddedModal}
                 width={644}
                 height={472}
                 gray
                 showCloseButton={false}
                 handleClose={() => undefined/*this.setState({shoppingCartItemAddedModal: false})*/}>
            <div className={style.shoppingCartItemAddedModal}>
              <h3>Producto agregado al carrito</h3>
              <div className={style.contentWrap}>
                <div className={style.topContent}>
                  <img className={style.product}
                       src={lastShoppingCartItemAdded.image}
                       alt={lastShoppingCartItemAdded.name}/>
                  <div className={style.textWrap}>
                    <h4>{lastShoppingCartItemAdded.name}</h4>
                    <p>Método de envío: {lastShoppingCartItemAdded.quoteMethodType === 'FULL' ? 'Qempo Full' : 'Qempo Lite'}</p>
                  </div>
                  {/*<img className={style.girl}
                       src={girl}
                       alt="girl"/>*/}
                </div>
                <hr/>
                <div className={style.bottomContent}>
                  <h4>Tenemos novedades para ti!</h4>
                  <p>
                    Hemos actualizado nuestro proceso de compra y ahora  nuestra plataforma solo te mostrara la
                    mejor opción de envio para tu pedido. Además ahora podrás agregar productos con diferentes
                    metodos de envío en tu carrito!
                  </p>
                </div>
              </div>
              <div className={style.buttonsWrap}>
                <Link className={classNames(buttonStyle.btn, buttonStyle.lightBlueInverse)}
                      to="/">
                  Seguir comprando
                </Link>
                <Link className={classNames(buttonStyle.btn, buttonStyle.lightBlue)}
                      to="/shopping-cart">
                  Ir al carrito de compras
                </Link>
              </div>
            </div>
          </Modal>
        )}
      </Fragment>
    );
  }
}

ProductStep.propTypes = {
  handleOpenAuthModal: PropTypes.func.isRequired,
  authModalVisible: PropTypes.bool,
  user: PropTypes.object,
  authenticated: PropTypes.bool,
  localeState: PropTypes.object.isRequired,
  localValues: PropTypes.object.isRequired,
  preloadedProduct: PropTypes.object,
  location: PropTypes.object.isRequired,
  fetchWishLists: PropTypes.func.isRequired,
  wishLists: PropTypes.array,
  postProductToWishList: PropTypes.func.isRequired,
  postNewWishList: PropTypes.func.isRequired,
  isPostingProduct: PropTypes.bool,
  isPostingNewWishList: PropTypes.bool,
  postProductSuccess: PropTypes.bool,
  postNewWishListError: PropTypes.object,
  postBagItem: PropTypes.func.isRequired,
  isPostingBag: PropTypes.bool,
  postBagItemError: PropTypes.object,
  postVirtualShoppingCartItem: PropTypes.func.isRequired,
  postShoppingCartItem: PropTypes.func.isRequired,
  isPostingCartItem: PropTypes.bool,
  postCartItemError: PropTypes.object,
  resetAlertState: PropTypes.func.isRequired,
  chosenVariants: PropTypes.object,
  virtualShoppingCart: PropTypes.array.isRequired,
  virtualBag: PropTypes.array,
  fetchVirtualBag: PropTypes.func.isRequired,
  postVirtualBagItem: PropTypes.func.isRequired,
  deleteVirtualBag: PropTypes.func.isRequired,
  goToRedirect: PropTypes.func.isRequired,
  scrapUrl: PropTypes.func.isRequired,
  isScrappingUrl: PropTypes.bool,
  scrappedProduct: PropTypes.object,
  scrapSuccess: PropTypes.bool,
  scrapUrlError: PropTypes.object,
  scrapResetState: PropTypes.func.isRequired,
  resetAlertErrorState: PropTypes.func.isRequired,
  forecastPrices: PropTypes.func.isRequired,
  forecast: PropTypes.object,
  isLoadingForecast: PropTypes.bool,
  checkoutFetchCheckoutDataSuccess: PropTypes.func.isRequired,
};

ProductStep.contextTypes = {
  intl: intlShape,
};



// WEBPACK FOOTER //
// ./src/modules/new-order/ProductStep/components/ProductStep.jsx